import {
    CRUDDetailsReducers,
    errorReducer,
    genericReducer,
    loadingReducer,
} from '../../common/helpers/ReduxHelpers';
import {
    DSAR_DETAILS_FIELD_IDS,
    DSAR_DISPLAY_OPTIONS,
    DSAR_STANDARD_FIELDS_I18N,
} from '/b2b/dsarForms/constants';
import {
    formatIdVerificationField,
    formatRepresentativeAuthorizationField,
    formatRepresentativeTypeField,
} from '/b2b/dsarForms/format';

import { types as ActionTypes } from './FormDetails.actions';
import { types as AuthActionTypes } from '/b2b/authentication/state/Auth.actions';
import AuthenticatedPaths from '../../routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import { types as NavActionTypes } from '../../common/state/nav/Nav.actions';
import cloneDeep from 'lodash/cloneDeep';
import { combineReducers } from 'redux';
import isNil from 'lodash/isNil';
import { matchPath } from 'react-router-dom';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import reduceReducers from 'reduce-reducers';
import schema from './db/Form.schema';
import set from 'lodash/set';
import { v4 as uuid } from 'uuid';
import { validateEmail } from '../../common/helpers/Email';

const { IDENTIFICATION_FILE, REPRESENTATIVE_AUTHORIZATION_FILE, REPRESENTATIVE_TYPE } =
    DSAR_DETAILS_FIELD_IDS;

// update excluded property on all representative type fields
const formatRepresentativeTypeFields = (requestTypes = [], customFields = [], fields = {}) => {
    const representativeTypeEnabled = requestTypes.some(
        ({ agent_submission_allowed, agentSubmissionAllowed = agent_submission_allowed }) =>
            agentSubmissionAllowed
    );

    const representativeTypeField = formatRepresentativeTypeField(
        { ...fields[REPRESENTATIVE_TYPE] },
        requestTypes
    );

    const newFields = {
        ...fields,
        [REPRESENTATIVE_TYPE]: representativeTypeField,
    };

    const newCustomFields = customFields.map(field => {
        if (field.id === REPRESENTATIVE_TYPE) {
            return representativeTypeField;
        } else if (field?.conditional?.id === REPRESENTATIVE_TYPE) {
            const newField = {
                ...field,
                excluded: !representativeTypeEnabled,
            };

            newFields[field.id] = newField;

            return newField;
        }
        return field;
    });

    return {
        customFields: newCustomFields,
        fields: newFields,
    };
};

const omitEmpty = obj => omitBy(obj, val => isNil(val) || val === '');

export const initialState = {
    id: '',
    tab: '',
    loading: false,
    updating: false,
    submitted: false,
    error: null,
    updateError: null,
    savedChanges: null,
    changes: {
        duplicateReject: true,
        unverifiedEmailReject: false,
        captchaEnabled: false,
        aiEmailParsing: false,
        internalDueDays: null,
        internalDueItems: null,
        name: null,
        description: null,
        // Seed `styling` so brand-new forms carry the same defined, editable
        // shape as persisted forms (which the API returns with a styling
        // object). Without this, `styling.instructions` had no parent object
        // on a new form until the first edit. See DSAR-6296.
        styling: { instructions: null },
        organizations: [],
        customFields: [],
        fields: {},
        requestTypes: [],
        displayOption: null,
        displayOptionRequestTypes: [],
        generatedInbox: '',
        allowListEmails: [],
        escalationEmails: [],
    },
    errors: {},
};

const clearFieldError =
    prefix =>
    (state, { id, field = '', value, oldValue } = { value: true }) => {
        if (value !== oldValue) {
            return omit({ ...state }, `${prefix}${id ? `${id}.` : ''}${field}`);
        }
        return state;
    };

export default reduceReducers(
    initialState,
    combineReducers({
        ...CRUDDetailsReducers(
            initialState,
            [
                ActionTypes.fetchDetailsBegin,
                ActionTypes.fetchDetailsSuccess,
                ActionTypes.fetchDetailsFailure,
            ],
            schema
        ),
        updating: loadingReducer(
            initialState.updating,
            [
                ActionTypes.createBegin,
                ActionTypes.createSuccess,
                ActionTypes.createFailure,
                ActionTypes.updateBegin,
                ActionTypes.updateSuccess,
                ActionTypes.updateFailure,
                ActionTypes.deleteBegin,
                ActionTypes.deleteSuccess,
                ActionTypes.deleteFailure,
            ],
            schema
        ),
        updateError: errorReducer(
            initialState.updateError,
            [
                ActionTypes.fetchDetailsBegin,
                ActionTypes.createBegin,
                ActionTypes.createSuccess,
                ActionTypes.createFailure,
                ActionTypes.updateBegin,
                ActionTypes.updateSuccess,
                ActionTypes.updateFailure,
                ActionTypes.deleteBegin,
                ActionTypes.deleteSuccess,
                ActionTypes.deleteFailure,
            ],
            schema
        ),
        submitted: genericReducer(initialState.submitted, {
            [ActionTypes.createBegin]: () => true,
            [ActionTypes.fetchDetailsBegin]: () => true,
            [ActionTypes.updateBegin]: () => true,
            [ActionTypes.createSuccess]: () => false,
            [ActionTypes.fetchDetailsSuccess]: () => false,
            [ActionTypes.updateSuccess]: () => false,
            [ActionTypes.fieldErrors]: () => true,
        }),
        tab: genericReducer(initialState.tab, {}),
        changes: genericReducer(initialState.changes, {
            [ActionTypes.addConditionalField]: (
                state,
                { id: dependentId, value: dependentValue, data }
            ) => {
                const { fields, customFields } = state;
                let newFields = [...customFields];
                const id = uuid('customFormField');
                const field = omitEmpty({
                    type: 'text',
                    required: false,
                    id,
                    ...data,
                    name: data.name.startsWith('o-') ? data.name : `o-${data.name}` || '',
                    conditional: {
                        id: dependentId,
                        value: dependentValue,
                    },
                });

                const parentIndex = newFields.findLastIndex(
                    ({ id: fieldId }) => fieldId === dependentId
                );
                const conditionIndex = newFields.findLastIndex(
                    ({ conditional: { id: conditionalId } = {} }) => conditionalId === dependentId
                );
                const optionIndex = newFields.findLastIndex(
                    ({ conditional: { id: conditionalId, value: conditionalValue } = {} }) =>
                        conditionalId === dependentId && conditionalValue === dependentValue
                );

                // insert priority optionIndex > conditionIndex > parentIndex
                const insertIndex =
                    optionIndex > -1
                        ? optionIndex + 1
                        : conditionIndex > -1
                          ? conditionIndex + 1
                          : parentIndex + 1;

                return {
                    ...state,
                    customFields: [
                        ...newFields.slice(0, insertIndex),
                        field,
                        ...newFields.slice(insertIndex),
                    ],
                    fields: {
                        ...fields,
                        [id]: field,
                    },
                };
            },
            [ActionTypes.updateConditionalField]: (state, { id, data }) => {
                const { fields, customFields } = state;

                let newField;
                const newCustomFields = customFields.reduce((acc, field) => {
                    if (field.id === id) {
                        const name =
                            DSAR_STANDARD_FIELDS_I18N[id] || data.name.startsWith('o-')
                                ? data.name
                                : `o-${data.name}` || '';
                        newField = {
                            ...field,
                            ...omitEmpty({
                                ...data,
                                name,
                            }),
                        };
                        return [...acc, newField];
                    }
                    return [...acc, field];
                }, []);

                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: {
                        ...fields,
                        [id]: newField,
                    },
                };
            },
            [ActionTypes.dragConditionalField]: (state, { id, dragIndex, hoverIndex }) => {
                const { customFields } = state;

                const newCustomFields = [...customFields];

                const trueDragIndex = newCustomFields.findIndex(
                    ({ id: fieldId }) => fieldId === id
                );

                const insertIndex =
                    dragIndex > hoverIndex
                        ? trueDragIndex - Math.abs(dragIndex - hoverIndex)
                        : trueDragIndex + Math.abs(dragIndex - hoverIndex);

                const field = newCustomFields.splice(trueDragIndex, 1);

                return {
                    ...state,
                    customFields: [
                        ...newCustomFields.slice(0, insertIndex),
                        ...field,
                        ...newCustomFields.slice(insertIndex),
                    ],
                };
            },
            [ActionTypes.addField]: (state, { data, index }) => {
                const { fields, customFields } = state;
                let newCustomFields = [...customFields];
                const id = uuid('customFormField');
                const field = {
                    label: '',
                    type: 'text',
                    required: false,
                    helperText: '',
                    id,
                    ...data,
                    name: `o-${data.name ? data.name : ''}`,
                };

                if (typeof index === 'number' && index > 0) {
                    const insertIndex = Math.max(0, Math.min(newCustomFields.length, index));
                    newCustomFields = [
                        ...newCustomFields.slice(0, insertIndex),
                        field,
                        ...newCustomFields.slice(insertIndex),
                    ];
                } else {
                    newCustomFields.unshift(field);
                }
                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: {
                        ...fields,
                        [id]: field,
                    },
                };
            },
            [ActionTypes.deleteField]: (state, { id }) => {
                const { fields, customFields } = state;

                const newFields = { ...fields };

                const fieldToDelete = newFields[id];

                let newCustomFields = [...customFields];

                // if we're deleting a conditional field, just delete that field
                if (fieldToDelete?.conditional?.id) {
                    delete newFields[id];
                    newCustomFields = customFields.filter(({ id: fieldId }) => fieldId !== id);
                } else {
                    // otherwise, delete field and all fields that depend on this one
                    customFields
                        .filter(
                            ({ id: fieldId, conditional: { id: conditionalId } = {} }) =>
                                fieldId === id || conditionalId === id
                        )
                        .forEach(({ id: fieldId }) => {
                            delete newFields[fieldId];
                        });

                    newCustomFields = customFields.filter(
                        ({ id: fieldId, conditional: { id: conditionalId } = {} }) =>
                            fieldId !== id && conditionalId !== id
                    );
                }

                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: newFields,
                };
            },
            [ActionTypes.dragFieldOption]: (state, { id, dragIndex, hoverIndex }) => {
                const { fields, customFields } = state;
                const fieldOptions = fields[id]?.options || [];
                const newOrder = [...fieldOptions];
                const dragged = newOrder.splice(dragIndex, 1);

                let newCustomFields = [...customFields];

                const dragFields = newCustomFields.filter(
                    ({ conditional: { id: conditionalId, value: conditionalValue } = {} }) =>
                        conditionalId === id && conditionalValue === fieldOptions[dragIndex].value
                );

                const hoverFields = newCustomFields.filter(
                    ({ conditional: { id: conditionalId, value: conditionalValue } = {} }) =>
                        conditionalId === id && conditionalValue === fieldOptions[hoverIndex].value
                );

                if (dragFields.length || hoverFields.length) {
                    const conditionDragIndex = newCustomFields.findIndex(
                        ({ id: fieldId }) => fieldId === dragFields[0].id
                    );

                    newCustomFields.splice(conditionDragIndex, dragFields.length);

                    const conditionHoverIndex = newCustomFields.findIndex(
                        ({ id: fieldId }) =>
                            fieldId ===
                            hoverFields[dragIndex > hoverIndex ? 0 : hoverFields.length - 1].id
                    );
                    const insertIndex =
                        dragIndex > hoverIndex ? conditionHoverIndex : conditionHoverIndex + 1;

                    newCustomFields = [
                        ...newCustomFields.slice(0, insertIndex),
                        ...dragFields,
                        ...newCustomFields.slice(insertIndex),
                    ];
                }

                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: {
                        ...fields,
                        [id]: {
                            ...fields[id],
                            options: [
                                ...newOrder.slice(0, hoverIndex),
                                ...dragged,
                                ...newOrder.slice(hoverIndex),
                            ],
                        },
                    },
                };
            },
            [ActionTypes.dragField]: (state, { dragIndex, hoverIndex }) => {
                if (dragIndex === hoverIndex) {
                    return state;
                }

                const { customFields } = state;
                const dragId = customFields[dragIndex].id;
                const dragFields = customFields.filter(
                    ({ id, conditional: { id: conditionalId } = {} }) =>
                        [id, conditionalId].includes(dragId)
                );

                const newCustomFields = [...customFields];
                newCustomFields.splice(dragIndex, dragFields.length);

                const hoverId = customFields[hoverIndex].id;
                const insertIndex =
                    dragIndex > hoverIndex
                        ? hoverIndex
                        : newCustomFields.findLastIndex(
                              ({ id, conditional: { id: conditionalId } = {} }) =>
                                  [id, conditionalId].includes(hoverId)
                          ) + 1;

                return {
                    ...state,
                    customFields: [
                        ...newCustomFields.slice(0, insertIndex),
                        ...dragFields,
                        ...newCustomFields.slice(insertIndex),
                    ],
                };
            },
            [ActionTypes.fetchDetailsSuccess]: (state, { response }) => {
                return {
                    ...state,
                    ...response,
                };
            },
            [ActionTypes.updateField]: (state, { id, field, value }) => {
                const { fields, customFields } = state;
                const newFields = { ...fields };
                let newCustomFields = [...customFields];
                switch (field) {
                    case 'name':
                        // We can only edit the names of custom fields
                        value = `o-${value}`;
                        break;
                    case 'type':
                        // If changing type, delete any dependent fields
                        customFields
                            .filter(
                                ({ conditional: { id: conditionalId } = {} }) =>
                                    conditionalId === id
                            )
                            .forEach(({ id: fieldId }) => {
                                delete newFields[fieldId];
                            });

                        newCustomFields = customFields.filter(
                            ({ conditional: { id: conditionalId } = {} }) => conditionalId !== id
                        );
                        break;
                    case 'displayOption':
                        // If changing display option, reset properties set by the previous display option
                        delete newFields[id].conditional;
                        delete newFields[id].displayOptionRequestTypes;
                        delete newFields[id].excluded;
                        if (value === DSAR_DISPLAY_OPTIONS.DISABLED) {
                            newFields[id].excluded = true;
                        }
                        break;
                    case 'displayOptionRequestTypes':
                        newFields[id].conditional = {
                            id: 'request-type',
                            value: value?.map(({ value }) => value),
                        };
                        break;
                }
                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: {
                        ...newFields,
                        [id]: {
                            ...newFields[id],
                            [field]: value,
                        },
                    },
                };
            },
            [ActionTypes.useDefaultOptions]: (state, { id }) => {
                const { fields } = state;
                const options =
                    DSAR_STANDARD_FIELDS_I18N[`${id}`]?.options || fields[id]?.options || [];

                return {
                    ...state,
                    fields: {
                        ...fields,
                        [id]: {
                            ...fields[id],
                            options,
                        },
                    },
                };
            },
            [ActionTypes.addFieldOption]: (state, { id }) => {
                const { fields } = state;
                const options = fields[id]?.options || [];

                return {
                    ...state,
                    fields: {
                        ...fields,
                        [id]: {
                            ...fields[id],
                            options: [
                                ...options,
                                {
                                    label: '',
                                    value: '',
                                },
                            ],
                        },
                    },
                };
            },
            [ActionTypes.deleteFieldOption]: (state, { id, optionIndex }) => {
                const { fields, customFields } = state;
                const options = fields[id]?.options || [];

                // remove any dependent fields
                const newCustomFields = customFields.filter(
                    ({ conditional: { id: conditionalId, value: conditionalValue } = {} }) =>
                        !(conditionalId === id && conditionalValue === options[optionIndex]?.value)
                );

                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: {
                        ...fields,
                        [id]: {
                            ...fields[id],
                            options: [
                                ...options.slice(0, optionIndex),
                                ...options.slice(optionIndex + 1),
                            ],
                        },
                    },
                };
            },
            [ActionTypes.updateFieldOption]: (state, { id, optionIndex, field, value }) => {
                const { fields, customFields } = state;
                const options = fields[id]?.options || [];

                const newFields = { ...fields };

                // keep conditional values in sync with option value
                const newCustomFields =
                    field === 'value'
                        ? customFields.map(field => {
                              if (
                                  field?.conditional?.id === id &&
                                  field?.conditional?.value === options[optionIndex]?.value
                              ) {
                                  newFields[field.id] = {
                                      ...field,
                                      conditional: {
                                          ...field.conditional,
                                          value,
                                      },
                                  };
                                  return {
                                      ...field,
                                      conditional: {
                                          ...field.conditional,
                                          value,
                                      },
                                  };
                              }
                              return field;
                          })
                        : customFields;

                return {
                    ...state,
                    customFields: newCustomFields,
                    fields: {
                        ...newFields,
                        [id]: {
                            ...newFields[id],
                            options: [
                                ...options.slice(0, optionIndex),
                                {
                                    ...options[optionIndex],
                                    [field]: value,
                                },
                                ...options.slice(optionIndex + 1),
                            ],
                        },
                    },
                };
            },
            [ActionTypes.hideFieldOption]: (state, { id, optionIndex }) => {
                const { fields } = state;
                const options = fields[id]?.options || [];
                const { hidden } = options[optionIndex];
                return {
                    ...state,
                    fields: {
                        ...fields,
                        [id]: {
                            ...fields[id],
                            options: [
                                ...options.slice(0, optionIndex),
                                {
                                    ...options[optionIndex],
                                    hidden: !hidden,
                                },
                                ...options.slice(optionIndex + 1),
                            ],
                        },
                    },
                };
            },
            [ActionTypes.updateRequestType]: (state, { id, field, value }) => {
                const { customFields, fields, requestTypes } = state;
                const newRequestTypes = requestTypes.map(requestType =>
                    requestType.id === id
                        ? {
                              ...requestType,
                              [field]: value,
                          }
                        : requestType
                );

                const idVerificationField = formatIdVerificationField(
                    { ...fields[IDENTIFICATION_FILE] },
                    newRequestTypes
                );

                const authorizationField = formatRepresentativeAuthorizationField(
                    { ...fields[REPRESENTATIVE_AUTHORIZATION_FILE] },
                    newRequestTypes
                );

                return {
                    ...state,
                    requestTypes: newRequestTypes,
                    ...(field === 'verificationLevel' && {
                        customFields: customFields.map(field => {
                            if (field.id === IDENTIFICATION_FILE) {
                                return idVerificationField;
                            }
                            return field;
                        }),
                        fields: {
                            ...fields,
                            [IDENTIFICATION_FILE]: idVerificationField,
                        },
                    }),
                    ...(field === 'agentVerificationLevel' && {
                        customFields: customFields.map(field => {
                            if (field.id === REPRESENTATIVE_AUTHORIZATION_FILE) {
                                return authorizationField;
                            }
                            return field;
                        }),
                        fields: {
                            ...fields,
                            [REPRESENTATIVE_AUTHORIZATION_FILE]: authorizationField,
                        },
                    }),
                    ...(field === 'agentSubmissionAllowed' && {
                        ...formatRepresentativeTypeFields(newRequestTypes, customFields, fields),
                    }),
                };
            },
            [ActionTypes.updateForm]: (state, { field, value, oldValue }) => {
                if (value === oldValue) {
                    return state;
                }
                if (field === 'organizations') {
                    const mapOrgs = ({ value: orgId = '', label: name = '' } = {}) => ({
                        orgId,
                        name,
                    });
                    return set(
                        {
                            ...state,
                        },
                        field,
                        (value || []).map(mapOrgs)
                    );
                }
                if (field === 'allowListEmails') {
                    if (value.length > 10) {
                        return state;
                    }
                    const newState = set(
                        {
                            ...state,
                        },
                        field,
                        [...new Set(value.filter(validateEmail))]
                    );
                    // remove any allowlisted emails from escalaton emails
                    return set(
                        {
                            ...newState,
                        },
                        'escalationEmails',
                        [
                            ...new Set(
                                newState.escalationEmails.filter(
                                    email =>
                                        validateEmail(email) &&
                                        !newState.allowListEmails.includes(email)
                                )
                            ),
                        ]
                    );
                }
                if (field === 'escalationEmails') {
                    if (value.length > 10) {
                        return state;
                    }
                    return set(
                        {
                            ...state,
                        },
                        field,
                        [
                            ...new Set(
                                value.filter(
                                    email =>
                                        validateEmail(email) &&
                                        !state.allowListEmails.includes(email)
                                )
                            ),
                        ]
                    );
                }
                if (field === 'internalDueItems' && value === 'internalDueDefault') {
                    return set(
                        {
                            ...state,
                            internalDueDays: null,
                        },
                        field,
                        value
                    );
                }
                return set(cloneDeep(state), field, value);
            },
        }),
        savedChanges: genericReducer(initialState.savedChanges, {}),
        errors: genericReducer(initialState.errors, {
            [ActionTypes.updateForm]: clearFieldError(''),
            [ActionTypes.updateField]: clearFieldError('fields.'),
            [ActionTypes.deleteField]: clearFieldError('fields.'),
            [ActionTypes.addFieldOption]: (state, { id }) => {
                const { fields = {} } = state;
                const options = fields[id]?.options;
                if (typeof options === 'string') {
                    return omit({ ...state }, `fields.${id}.options`);
                }
                return state;
            },
            [ActionTypes.useDefaultOptions]: (state, { id }) =>
                clearFieldError('fields.')(state, { id, field: `options`, value: true }),
            [ActionTypes.deleteFieldOption]: (state, { id, optionIndex, value }) =>
                clearFieldError('fields.')(state, { id, field: `options.${optionIndex}`, value }),
            [ActionTypes.updateFieldOption]: (state, { id, optionIndex, field, value }) =>
                clearFieldError(`fields.`)(state, {
                    id,
                    field: `options.${optionIndex}.${field}`,
                    value,
                }),
            [ActionTypes.dragFieldOption]: (state, { id, dragIndex, hoverIndex }) => {
                const { fields = {} } = state;
                const fieldOptions = Object.entries(fields[id]?.options || {}).sort(
                    ([indexA], [indexB]) => parseInt(indexA, 10) - parseInt(indexB, 10)
                );
                return {
                    ...state,
                    fields: {
                        ...fields,
                        ...(fields[id]
                            ? {
                                  [id]: {
                                      ...fields[id],
                                      ...(fieldOptions.length
                                          ? {
                                                options: fieldOptions.reduce(
                                                    (newOptions, [index, value]) => {
                                                        let idx = parseInt(index, 10);
                                                        if (idx === dragIndex) {
                                                            idx = hoverIndex;
                                                        } else if (
                                                            idx > dragIndex &&
                                                            idx < hoverIndex
                                                        ) {
                                                            idx--;
                                                        } else if (idx >= hoverIndex) {
                                                            idx++;
                                                        }
                                                        newOptions[`${idx}`] = value;
                                                        return newOptions;
                                                    },
                                                    {}
                                                ),
                                            }
                                          : {}),
                                  },
                              }
                            : {}),
                    },
                };
            },
            [ActionTypes.fieldErrors]: (state, errors) => ({ ...errors }),

            [ActionTypes.fetchDetailsSuccess]: () => {
                return {};
            },
        }),
    }),
    genericReducer(initialState, {
        [ActionTypes.fetchDetailsSuccess]: state => ({
            ...state,
            savedChanges: cloneDeep(state.changes),
        }),
        [ActionTypes.updateSuccess]: state => ({
            ...state,
            savedChanges: cloneDeep(state.changes),
        }),
        [ActionTypes.discardChanges]: state => ({
            ...state,
            changes: state.savedChanges ? cloneDeep(state.savedChanges) : state.changes,
            errors: {},
        }),
        [AuthActionTypes.logout]: () => initialState,
        [NavActionTypes.routeChanged]: (state, payload) => {
            const { location, action } = payload;
            const match = matchPath(
                location.pathname,
                `${AuthenticatedPaths.DSAR_FORMS}/:formId?/:tab?`
            );
            const { params: { formId, tab = 'settings' } = {} } = match || {};
            if (action === 'LOAD' && match) {
                return {
                    ...state,
                    id: formId,
                    tab,
                };
            }
            if (!match || !formId || (formId === 'new' && state.id !== formId)) {
                return { ...initialState, id: match ? formId : '' };
            }
            if (state.id === formId && state.tab && state.tab !== tab) {
                return {
                    ...state,
                    id: formId,
                    tab,
                };
            }
            return state;
        },
    })
);
